'use client'

import { useMemo, useState } from 'react'

import { JOURNEY_SORT_OPTIONS } from '../constants/sort'
import { sortJourneys, type SortOption } from '../domain/sortJourneys'
import { useActiveJourney } from '../hooks/useActiveJourney'
import { useJourneysStore } from '../stores/useJourneysStore'
import { JourneyCard } from './JourneyCard'
import { JourneysPassFooter } from './JourneysPassFooter'

import type { Journey } from '@/features/journeys'
import { Card, CardBody, CardHeader, CardTitle } from '@/shared/ui/Card'
import { Page } from '@/shared/ui/Page'
import { SortDropdown } from '@/shared/ui/SortDropdown'
import { UnavailableState } from '@/shared/ui/UnavailableState'

interface JourneysListProps {
  onSelectJourney: (journey: Journey) => void
}

export function JourneysList({ onSelectJourney }: JourneysListProps) {
  const { journeys, isSuccess, isLoading } = useJourneysStore()
  const { activeJourneyId } = useActiveJourney()
  const [sortOption, setSortOption] = useState<SortOption>('recent')

  const sortedJourneys = useMemo(
    () => sortJourneys(journeys, sortOption),
    [journeys, sortOption]
  )

  return (
    <Page className="bg-[#378bea]">
      <div className="flex flex-col gap-4 w-full items-center min-w-0 max-w-full">
        <Card className="w-full max-w-2xl">
          <CardHeader>
            <CardTitle>Jornadas</CardTitle>
          </CardHeader>
          <CardBody className="gap-3">
            <div className="relative bg-[url('/misc/robot-fyk.png')] bg-contain bg-start bg-no-repeat ps-24 sm:ps-28 lg:ps-35 pt-4 sm:pt-6 lg:pt-10 h-[120px] sm:h-[150px] lg:h-[200px] flex flex-col justify-center">
              <span className="text-base lg:text-xl font-bold">
                Vamos Iniciar?
              </span>
              <span className="text-sm lg:text-md font-semibold text-gray-500 lg:max-w-sm">
                Escolha uma jornada e comece sua aventura gamificada.
              </span>
            </div>

            <div className="flex items-center justify-between gap-2">
              <h2 className="text-base lg:text-xl font-bold truncate">
                Suas Jornadas ({sortedJourneys.length})
              </h2>
              <SortDropdown
                value={sortOption}
                options={JOURNEY_SORT_OPTIONS}
                onChange={value => setSortOption(value as SortOption)}
                className="shrink-0 whitespace-nowrap"
                labelClassName="hidden sm:inline"
              />
            </div>

            {!isSuccess || isLoading ? (
              <p className="text-center py-12 text-sm font-medium text-[#7F7FAD]">
                Carregando...
              </p>
            ) : sortedJourneys.length === 0 ? (
              <UnavailableState
                title="Nenhuma jornada por aqui ainda"
                description="Assim que uma jornada for liberada para você, ela aparece nesta lista."
                className="min-h-[240px]"
              />
            ) : (
              <div className="space-y-3">
                {sortedJourneys.map(journey => (
                  <JourneyCard
                    key={journey.id}
                    journey={journey}
                    showCover={!!journey.cover}
                    isActive={activeJourneyId === journey.id}
                    onSelect={() => onSelectJourney(journey)}
                  />
                ))}
              </div>
            )}
          </CardBody>
        </Card>

        <div className="w-full max-w-2xl">
          <JourneysPassFooter />
        </div>
      </div>
    </Page>
  )
}
